import { type ReactNode, useMemo } from 'react';

import { ButtonComponentActionConfigVariant } from '@repo/sdk/models/components';

import { DropdownCell, type DropdownCellOption } from '@/components/data-table';
import { ActionIcon } from '@/features/equipment-browser/action-components/action-icon';
import type { FaultedDevice } from '@/features/equipment-browser/faulted-devices/faulted-devices.types';
import {
  EMPTY_CELL_LABEL,
  SELECT_MODE_PLACEHOLDER,
} from '@/features/equipment-browser/reset-faulted/reset-faulted.constants';
import { getModeOptions } from '@/features/equipment-browser/reset-faulted/reset-faulted.utils';
import { mapLegacyButtonVariantToClassName } from '@/features/equipment-browser/utils';
import { Typography } from '@/ux-common/components/typography/typography';
import { cn } from '@/ux-common/lib/utils';

interface ResetFaultedModeCellProps {
  device: FaultedDevice;
  onSelect: (deviceId: string, mode: string) => void;
  value?: string;
}

/**
 * Mode the device runs in after reset. Shows the device's mode options with the
 * current mode preselected; modeless devices show a dash. Disabled while the
 * device's fault cannot be reset.
 */
export const ResetFaultedModeCell = ({
  device,
  onSelect,
  value,
}: ResetFaultedModeCellProps): ReactNode => {
  const modeOptions = getModeOptions(device);

  const options = useMemo<DropdownCellOption[]>(
    () =>
      modeOptions.map((option) => ({
        icon: option.icon ? (
          <ActionIcon icon={option.icon.icon} variant={option.icon.variant} />
        ) : undefined,
        label: option.label,
        value: option.value,
      })),
    [modeOptions]
  );

  if (options.length === 0) {
    return (
      <Typography
        as="span"
        className="text-body-sm text-foreground px-gutter-sm"
        data-component="ResetFaultedModeCell"
      >
        {EMPTY_CELL_LABEL}
      </Typography>
    );
  }

  const disabled = !device.isResettable;

  // DropdownCell's trigger wrapper fills the cell height and pins the button to
  // the top; letting it size to content centres the button in the row.
  return (
    <div className="flex items-center h-full w-full px-gutter-sm [&>[data-component=dropdown-cell]]:h-auto">
      <DropdownCell
        // No text colour here: the tier2 variant pairs a foreground with each of
        // its backgrounds, and a `text-*` utility would override the hover state.
        className={cn(
          mapLegacyButtonVariantToClassName(ButtonComponentActionConfigVariant.Tier2),
          'w-full text-sm',
          disabled && 'cursor-not-allowed opacity-50'
        )}
        data-component="ResetFaultedModeCell"
        disabled={disabled}
        onSelect={(option) => onSelect(device.summary.referenceId, option.value)}
        options={options}
        placeholder={SELECT_MODE_PLACEHOLDER}
        subtitle={device.modeDropdown?.subtitle}
        tooltipContent={disabled ? device.disabledMessage : undefined}
        value={value}
      />
    </div>
  );
};
